import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import SelectPropertiesDrawerV2, {
  SelectionAdapter
} from 'components/SelectPropertiesDrawerV2';
import SelectPropertiesDrawer from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/components/SelectPropertiesDrawer';
import EmptyTableState from 'components/EmptyTableState';
import NoCropZonesSelectedIcon from 'assets/properties-map-icon.svg';
import PlusIcon from 'assets/plus.svg?react';
import CropZonesTable from 'components/CropZonesTable';
import TotalAcreageSummary from 'components/CropZonesTable/TotalAcreageSummary';
import { getMappedData } from 'components/CropZonesTable/helpers';
import { calculateTotalAppliedAcreage } from 'screens/Applications&WorkOrders/StepsLayout/Components/PropertiesStep/helpers/cropZoneMath';
import LayoutConstants from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutConstants';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { useStore } from 'components/Store';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { PlanWizardFormValues, PlanCropZone } from '../../types';
import applyPlannedAreaChange from './helpers/applyPlannedAreaChange';
import useResolveStubCropZones from './hooks/useResolveStubCropZones';

/**
 * Bridges `SelectPropertiesDrawerV2` to the Plan wizard's Formik form,
 * mirroring `useOperationSelectionAdapter`'s role for Applications/Work Orders.
 */
const usePlanSelectionAdapter = (): SelectionAdapter => {
  const { values, setFieldValue } = useFormikContext<PlanWizardFormValues>();

  return useMemo(
    () => ({
      initialCropZones: values.cropZones,
      existingCropZones: values.cropZones,
      onConfirm: ({ cropZones }: { cropZones: PlanCropZone[] }) => {
        setFieldValue('cropZones', cropZones);
      }
    }),
    [values.cropZones, setFieldValue]
  );
};

/** FR-066: relabeled from "Select Properties" to match Applications/Work Orders' own copy. */
const SelectPropertiesButton: React.FC<{
  onClick: () => void;
  ghost?: boolean;
}> = ({ onClick, ghost = false }) => (
  <Button
    type={ButtonType.primary}
    data-testid="select-properties-button"
    style={{ background: ghost ? 'white' : undefined }}
    icon={<PlusIcon fill={ghost ? '#14803C' : 'white'} />}
    onClick={onClick}
    ghost={ghost}
  >
    Select Crop Zones
  </Button>
);

/** Matches Applications/Work Orders' own legacy-drawer `formikValues` mapping (FR-066) — `cropZones` is Plans' single source of truth for the selection, so both slots point at it. */
const LEGACY_DRAWER_FORMIK_VALUES = {
  cropZoneIds: 'cropZones',
  totalArea: 'totalAppliedAcreage',
  cropZones: 'cropZones',
  sourceType: 'cropZoneSourceType'
};

/**
 * Properties step of the Plan creation wizard (FR-013): replaces the
 * checklist-style picker with the searchable tree-style
 * `SelectPropertiesDrawerV2`, matching Applications/Work Orders.
 */
const PropertiesStep: React.FC = () => {
  const { values, touched, setFieldValue } = useFormikContext<
    PlanWizardFormValues
  >();
  useResolveStubCropZones();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [totalAppliedAcreage, setTotalAppliedAcreage] = useState(
    values.totalAppliedAcreage || 0
  );
  const selectionAdapter = usePlanSelectionAdapter();

  // Keeps this in sync when totalAppliedAcreage changes outside the
  // user-driven handleTotalAcreageChange path below — e.g.
  // useResolveStubCropZones's mismatch filter setting it on mount.
  useEffect(() => {
    setTotalAppliedAcreage(values.totalAppliedAcreage || 0);
  }, [values.totalAppliedAcreage]);

  // FR-066: scope the tree drawer's selection to the currently selected
  // crop season(s), and respect the same legacy-drawer flag Applications/
  // Work Orders checks before falling back to its checklist-style picker.
  const flags = useFlags();
  const {
    state: { organization }
  } = useStore();
  const showNewSelectPropertiesDrawer = featureFlagRenderItem(
    flags.releaseFeNewPropertiesDrawer,
    flags.releaseFeNewPropertiesDrawerByOrg,
    organization?.id
  );
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const touchedRef = useRef(touched);
  touchedRef.current = touched;

  const drawerInitialZoneIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (isDrawerOpen) {
      drawerInitialZoneIdsRef.current = (valuesRef.current.cropZones ?? []).map(
        z => z.id
      );
    }
  }, [isDrawerOpen]);

  /**
   * Keeps Planned Area matching Total Applied Acreage unless the user has
   * already customized Planned Area (`DetailsStep` marks it touched).
   * Wired into this explicit callback, not a `values`-watching effect, so it
   * can't fire on initial edit-mode hydration and clobber a saved value.
   */
  const handleTotalAcreageChange = useCallback(
    (total: number) => {
      setTotalAppliedAcreage(total);
      setFieldValue('totalAppliedAcreage', total);
      if (!touchedRef.current.estimatedAreaValue) {
        applyPlannedAreaChange(valuesRef.current, setFieldValue, total);
      }
    },
    [setFieldValue]
  );

  const handleUpdate = (data: PlanCropZone[]) => {
    setFieldValue('cropZones', data);
  };

  const handleSelectProperties = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);

    // Defer reading values until after React processes the drawer's
    // setFieldValue('cropZones', ...) — handleCloseDrawer runs synchronously
    // right after, so valuesRef.current would otherwise still hold the
    // pre-drawer state and we'd compute a stale (often 0) total.
    requestAnimationFrame(() => {
      const currentZones = valuesRef.current.cropZones ?? [];
      const initialIds = drawerInitialZoneIdsRef.current ?? [];
      const currentIds = currentZones.map(z => z.id);

      const zonesChanged =
        currentIds.length !== initialIds.length ||
        currentIds.some(id => !initialIds.includes(id));

      if (zonesChanged) {
        handleTotalAcreageChange(calculateTotalAppliedAcreage(currentZones));
      }
      drawerInitialZoneIdsRef.current = null;
    });
  };

  const isEmptyState = !values.cropZones || values.cropZones.length === 0;

  useEffect(() => {
    if (
      values.cropZones?.length &&
      values.cropZones.some(
        zone => zone.appliedAcreage == null && zone.coveragePercent == null
      )
    ) {
      setFieldValue('cropZones', getMappedData(values.cropZones));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.cropZones]);

  useEffect(() => {
    if (isEmptyState) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyState]);

  return (
    <div data-testid="plan-properties-step">
      {isEmptyState ? (
        <div className="bg-white shadow-sm-card rounded-lg w-full">
          <EmptyTableState
            title="No Crop Zones Selected"
            message="Add crop zones to your record."
            overlayIcon={NoCropZonesSelectedIcon}
            button={<SelectPropertiesButton onClick={handleSelectProperties} />}
          />
        </div>
      ) : (
        <TotalAcreageSummary
          totalAppliedAcreage={totalAppliedAcreage}
          cropZones={values.cropZones}
          setFieldValue={setFieldValue}
          onTotalAcreageChange={handleTotalAcreageChange}
          addButton={
            <SelectPropertiesButton ghost onClick={handleSelectProperties} />
          }
        >
          <CropZonesTable
            data={values.cropZones}
            onTotalAcreageChange={handleTotalAcreageChange}
            onDataChange={handleUpdate}
            getDeleteButtonLabel={record =>
              `Remove ${record.name ?? 'crop zone'}`
            }
            scroll={{ y: LayoutConstants.TABLE_VERTICAL_CROP_ZONES_SCROLL }}
          />
        </TotalAcreageSummary>
      )}
      {showNewSelectPropertiesDrawer ? (
        <SelectPropertiesDrawerV2
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          selectionAdapter={selectionAdapter}
          seasonIds={selectedCropSeasonIds}
        />
      ) : (
        <SelectPropertiesDrawer
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          seasonIds={selectedCropSeasonIds}
          formikValues={LEGACY_DRAWER_FORMIK_VALUES}
        />
      )}
    </div>
  );
};

export default PropertiesStep;
